/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { lodash, propsUtils, keyboardUtils } from '@ba-ui-toolkit/utils';
import Button from '@ba-ui-toolkit/button';
import Collapse from '@ba-ui-toolkit/collapse';
import SVGIcon from '@ba-ui-toolkit/svgicon';
import chevronRight from '@ba-ui-toolkit/ba-graphics/dist/icons-js/chevron-right_16';
import Container from '@ba-ui-toolkit/container';
import PropertiesPane from '../PropertiesPane';
import styles from './SectionStyles.scss';

const collapsibleSectionClassName = styles.collapsibleSectionClassName.replace(/"/g, ''); // eslint-disable-line no-unused-vars

const { uniqueId, omit } = lodash;
const { sanitize, getInheritedProps } = propsUtils;
const { getKeyName, keyNames } = keyboardUtils;

export default class CollapsibleSection extends Component {
	static propTypes = {
		label: PropTypes.string.isRequired,
		children: PropTypes.node.isRequired,
		title: PropTypes.string,
		defaultOpen: PropTypes.bool,
		open: PropTypes.bool,
		rtl: PropTypes.bool,
		variant: PropTypes.oneOf(['normal', 'header']),
		onOpen: PropTypes.func,
		className: PropTypes.string,
		style: PropTypes.object,
		labelStyle: PropTypes.object,
		autoUnmount: PropTypes.bool,
		...omit(Collapse.propTypes, 'isOpened'),
	};

	static defaultProps = {
		variant: 'normal',
		className: '',
		style: {},
		labelStyle: {},
	};

	constructor(props) {
		super(props);
		this.state = {
			open: !!this.props.defaultOpen || !!this.props.open,
		};
		this._id = uniqueId('__collapsibleSection_');
		this._propLabelId = `${this._id}_propLabel_`;
	}

	componentWillReceiveProps({ open }) {
		if (open != null && open !== this.state.open) {
			this.setState({ open });
		}
	}

	_toggleOpen = () => {
		const { open, onOpen } = this.props;
		if (typeof open === 'undefined') {
			this.setState({ open: !this.state.open });
		}
		onOpen && onOpen(!open);
	};

	_onKeyDown = e => {
		const { open } = this.state;
		switch (getKeyName(e)) {
			case keyNames.LEFT:
			case keyNames.UP:
				e.preventDefault();
				if (open) {
					this._toggleOpen();
				}
				break;
			case keyNames.RIGHT:
			case keyNames.DOWN:
				e.preventDefault();
				if (!open) {
					this._toggleOpen();
				}
				break;
			default:
		}
	};

	render() {
		const {
			label,
			title,
			rtl,
			variant,
			children,
			className,
			style,
			labelStyle,
			autoUnmount,
			...others
		} = this.props;
		const { open } = this.state;
		const regionId = `${this._id}_region`;
		const collapsePassthroughProps = getInheritedProps(others, Collapse);
		const buttonPassthoughProps = getInheritedProps(others, Button);
		const propertiesPanePassthoughProps = getInheritedProps(others, PropertiesPane);

		return (
			<Container
				{...sanitize(others, CollapsibleSection)}
				className={`ba-common-collapsibleSection ${className}`}
				style={style}
			>
				<PropertiesPane
					{...propertiesPanePassthoughProps}
					label={label}
					title={title}
					rtl={rtl}
					htmlFor={this._id}
					labelSize="large"
					labelId={this._propLabelId}
					variant={variant}
					labelStyle={labelStyle}
				>
					<Button
						data-tid="ba-common-collapse-section__trigger-button"
						className={`${collapsibleSectionClassName}__trigger-button`}
						{...buttonPassthoughProps}
						id={this._id}
						variant="inline"
						size="small"
						icon={
							<SVGIcon
								iconId={chevronRight.id}
								size="small"
								rotate={open ? -90 : 90}
							/>
						}
						onClick={this._toggleOpen}
						aria-controls={regionId}
						aria-expanded={open}
						onKeyDown={this._onKeyDown}
						aria-labelledby={this._propLabelId}
					/>
				</PropertiesPane>
				<Collapse
					{...collapsePassthroughProps}
					autoUnmount={autoUnmount}
					isOpened={open}
					id={regionId}
				>
					{children}
				</Collapse>
			</Container>
		);
	}
}
